export const supportedLanguages = [
  { code: 'en', label: 'English', icon: '🇬🇧' },
  { code: 'fr', label: 'Français', icon: '🇫🇷' },
  { code: 'zh', label: '中文', icon: '🇨🇳' },
  { code: 'es', label: 'Español', icon: '🇪🇸' },
];

export const translations = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      einvoicing: 'E-Invoicing',
      projects: 'Projects',
      contact: 'Contact',
      about: 'About',
    },
    sections: {
      services: 'Services',
      projects: 'Projects',
      about: 'About',
      contact: 'Contact',
    },
    home: {
      roles: 'FIND YOUR SOLUTION',
      description:
        'Scientific consulting, AI-augmented web development, and selected digital services for businesses and technical teams.',
    },
    spotlight: {
      eyebrow: 'Additional service',
      title: 'Electronic invoicing support',
      summary:
        'A focused offer to review invoice data, detect missing compliance fields, generate Factur-X deliverables, and prepare rollout before live PDP integration.',
      highlights: [
        'Invoice compliance review',
        'Missing-field detection',
        'Factur-X generation',
        'Rollout guidance',
      ],
      primary: 'Book a demo',
      secondary: 'Contact us',
      price: 'Pilot offer from EUR 490',
      panelTitle: 'Included in this service',
    },
    einvoicingPage: {
      eyebrow: 'Digital invoicing',
      title: 'A dedicated page to explain, demo, price, and frame compliant e-invoicing rollout.',
      summary:
        'This offer is designed for small and mid-sized businesses that need a concrete e-invoicing setup: explanation of the process, a visual invoice demo, pricing options, compliance checkpoints, and supported payment flows including Stripe and bank transfer.',
      metrics: [
        { value: 'Factur-X', label: 'Structured PDF/XML output' },
        { value: '30 min', label: 'Typical discovery demo' },
        { value: 'PDP-ready', label: 'Prepared for connector phase' },
      ],
      primary: 'Book a demo',
      secondary: 'Back to services',
      price: 'Pilot package from EUR 490',
      panelTitle: 'Included in the dedicated page',
      highlights: [
        'Clear explanation of the e-invoicing workflow',
        'Interactive invoice demo with live totals',
        'Pricing tiers for pilot, rollout, and support',
        'Legal and payment blocks ready for review',
      ],
      complianceTitle: 'Compliance focus',
      complianceChips: ['Factur-X', 'Mandatory fields', 'Payment terms', 'Archiving-ready'],
      explainerEyebrow: 'Explanation',
      explainerTitle: 'What this page covers',
      explainerCards: [
        {
          title: 'Operational clarity',
          body: 'Show what changes between a classic PDF invoice, a structured invoice, and the future exchange flow with a platform.',
        },
        {
          title: 'Commercial framing',
          body: 'Turn a technical reform into a concrete offer page with visible pricing, expected deliverables, and a demo that reduces friction for prospects.',
        },
        {
          title: 'Deployment readiness',
          body: 'List the data to prepare now: issuer identity, VAT, invoice numbering, payment rails, statuses, and future routing to a PDP.',
        },
      ],
      demoEyebrow: 'Interactive demo',
      demoTitle: 'Live invoice preview',
      demoSummary:
        'The inputs below drive a mock invoice, legal tags, payment information, and generated visuals that can later be exported as assets or screenshots.',
      form: {
        issuer: 'Issuer',
        client: 'Client',
        invoiceNumber: 'Invoice number',
        amount: 'Amount ex. VAT',
        vatRate: 'VAT rate %',
        dueDays: 'Payment term in days',
        paymentMethod: 'Payment method',
        format: 'Output format',
        notes: 'Internal note',
      },
      paymentMethods: [
        { value: 'stripe', label: 'Stripe payment link' },
        { value: 'transfer', label: 'Bank transfer' },
        { value: 'sepa', label: 'SEPA direct debit' },
        { value: 'card', label: 'Card on file' },
      ],
      formats: [
        { value: 'factur-x', label: 'Factur-X' },
        { value: 'pdf-a3', label: 'PDF/A-3' },
        { value: 'ubl', label: 'UBL' },
        { value: 'cii', label: 'CII' },
      ],
      preview: {
        title: 'Invoice preview',
        client: 'Client',
        format: 'Format',
        payment: 'Payment',
        subtotal: 'Subtotal',
        vat: 'VAT',
        total: 'Total due',
        tags: ['Structured data', 'Ready for validation', 'Payment status traceable'],
      },
      generatedImages: [
        {
          label: 'Generated visual',
          title: 'Landing hero card',
          body: 'Branded visual generated from invoice metadata for a sales page or proposal deck.',
        },
        {
          label: 'Generated visual',
          title: 'Compliance tile',
          body: 'Compact card to highlight mandatory fields, due date, and selected payment flow.',
        },
        {
          label: 'Generated visual',
          title: 'Client summary',
          body: 'Preview block that can be reused in onboarding, emails, or a Stripe checkout handoff.',
        },
      ],
      pricingEyebrow: 'Pricing',
      pricingTitle: 'Three offer levels',
      pricingCards: [
        {
          title: 'Starter audit',
          price: 'EUR 490',
          summary: 'For one entity that needs a concrete starting point.',
          items: ['Invoice sample review', 'Missing-field checklist', 'Demo page setup'],
        },
        {
          title: 'Implementation sprint',
          price: 'EUR 1,490',
          summary: 'For teams that want page content, demo, and rollout framing.',
          items: ['Dedicated section design', 'Factur-X demo wiring', 'Payment flow framing'],
          featured: true,
        },
        {
          title: 'Monthly support',
          price: 'EUR 290 / month',
          summary: 'For iterative updates while the compliance setup matures.',
          items: ['Content updates', 'New payment blocks', 'Pre-PDP checklist follow-up'],
        },
      ],
      legalEyebrow: 'Legal and compliance',
      legalTitle: 'Key legal points to display',
      legalItems: [
        {
          title: 'Issuance obligation',
          body: 'From September 1, 2026, large and medium-sized companies in France must issue electronic invoices, while all companies must be able to receive them.',
        },
        {
          title: 'SME timeline',
          body: 'From September 1, 2027, small and micro businesses are expected to join invoice issuance obligations, which makes early preparation useful.',
        },
        {
          title: 'Scope',
          body: 'The page should state that legal information is informational, that accounting validation remains with the client, and that final routing depends on the selected platform and accounting stack.',
        },
      ],
      paymentEyebrow: 'Payment methods',
      paymentTitle: 'Supported payment positioning',
      paymentSummary:
        'The offer can present Stripe payment links, bank transfer, SEPA debit, or card-on-file workflows. The page is structured so you can show the commercial flow now even before final payment orchestration is automated.',
      paymentBadges: ['Stripe', 'Bank transfer', 'SEPA', 'Invoice terms', 'Future webhook sync'],
    },
    contact: {
      title: 'Contact',
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: 'Company Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Email sent successfully!',
      error: 'Failed to send email. Please try again.',
    },
    projects: {
      showMore: 'Show More',
      noProjects: 'No projects available.',
      noDescription: 'No description available.',
      visitWebsite: 'Visit Website',
      items: {
        topographicReconstruction: {
          title: 'Ultrasound Topographic Reconstruction',
          bodyText:
            '- This project explored ultrasound-based topographic reconstruction for dental applications such as digital impressions.\n- A high-frequency experimental setup was assessed as an alternative to optical systems and conventional molds in environments affected by saliva, fluids, and patient discomfort.\n\nKey aspects included:\n\n1. **Experimental setup**: ultrasonic reconstruction of dental surfaces was compared with optical references such as confocal microscopy and intraoral scanners.\n\n2. **Reconstruction methods**: Time of Flight (TOF) and Iterative Closest Point (ICP) methods were evaluated to improve geometric accuracy.\n\n3. **Comparison with optical systems**: ultrasound offered penetration and robustness in difficult conditions, while optical systems still provided finer surface resolution.\n\n4. **SAFT processing**: Synthetic Aperture Focusing Technique was used to extend depth of field and stabilize resolution outside the focal zone.\n\nThe objective was to assess ultrasound as a credible alternative for complex dental surface capture with improved depth capability.',
        },
        printedPhantoms: {
          title: '3D-Printed Phantoms for High-Frequency Ultrasound Imaging',
          bodyText:
            '- This study assessed 3D photopolymer jetting to create custom phantoms for ultrasound imaging above 20 MHz.\n- The work addressed the lack of commercial phantoms suitable for high-frequency imaging constraints.\n\nKey aspects included:\n\n1. **3D printing process**: photopolymer jetting enabled precise geometries and controlled structures for acoustic testing.\n\n2. **Material characterization**: six photopolymers were measured for impedance and attenuation to approximate tissue-like behavior.\n\n3. **Acoustic evaluation**: a 15 to 55 MHz focused transducer and dedicated amplification chain were used for reliable measurements.\n\n4. **Imaging performance**: the phantoms showed realistic acoustic behavior and signal-to-noise ratios up to 50 dB.\n\nThe study showed that 3D printing is a fast and practical route for calibration and validation tools in high-frequency ultrasound.',
        },
        piezoelectricTransducer: {
          title: 'Design and Fabrication of a Piezoelectric Transducer',
          bodyText:
            '- This research focused on a mono-element transducer based on the piezoelectric copolymer P(VDF-TrFE) for high-frequency ultrasound.\n- The target was an intraoral probe with a resonance close to 40 MHz and an active diameter of 4 mm.\n\nKey aspects included:\n\n1. **Modeling and simulation**: KLM equivalent-circuit modeling and finite element analysis were used to predict performance and optimize the stack.\n\n![bandWidth](images/projects/BW.webp)\n\n2. **Material choice**: P(VDF-TrFE) was selected for its lower acoustic impedance, flexibility, and mechanical resilience.\n\n3. **Fabrication**: the assembly combined pressing methods, 3D-printed backings, and epoxy bonding.\n\n4. **Validation**: imaging results were comparable to commercial references for soft tissues, with reduced sensitivity below 2 mm targets.\n\n![Transducer Image](images/projects/3d_us_image_mono.webp)\n\nThe work demonstrated the viability of custom transducers for compact high-frequency intraoral imaging.',
        },
        annularArray: {
          title: 'Evaluation of an Annular Array P(VDF-TrFE) on Silicon',
          bodyText:
            '- This study investigated high-frequency ultrasonic devices based on P(VDF-TrFE) deposited on silicon wafers.\n\n- **Design and fabrication**: spin-coated copolymer layers were patterned into annular arrays with concentric electrodes to limit coupling and improve acoustic behavior.\n\n- **Characterization**: electro-acoustic response and vibration profiles were measured with interferometry and impedance analysis.\n\n![Transducer Image](images/projects/Annu_spectro.webp)\n\n- **Modeling**: DREAM simulations were used to estimate pressure fields in water around 40 MHz for different focal distances.\n\n- **Next steps**: the study identified polarization and material-composition improvements to increase performance for integrated ultrasonic systems.',
        },
        probeEfficiency: {
          title:
            'Acoustic and Electrical Measurement of a Phased Array Ultrasonic Medical Probe for Efficiency Calculation',
          bodyText:
            '- This project examined efficiency estimation for a phased-array medical probe through combined acoustic and electrical measurements.\n\n- **Acoustic measurements**: azimuth-elevation scans were performed at geometric focus with one active element. A membrane hydrophone captured 100 x 240 waveforms over a 24 x 10 mm area, filtered between 3 and 13 MHz.\n\n![PII](images/projects/PII_xy.webp)\n\n- **Electrical measurements**: impedance and voltage data were acquired to evaluate calibration quality and signal fidelity.\n\n- **Efficiency calculation**: spectral energy density ratios were used to compare emitted acoustic energy with electrical input power.\n\n- **Recommendations**: the study suggested refinements in sampling, averaging, and spectral analysis to improve pulse-based efficiency measurements.\n\nThis work underlined the importance of rigorous electro-acoustic characterization for clinical probe optimization.',
        },
        saftAlgorithm: {
          title: 'SAFT Algorithm for Enhanced Depth of Field in Ultrasonic Imaging',
          bodyText:
            '- This project improved depth of field in ultrasound imaging using Synthetic Aperture Focusing Technique (SAFT).\n\n- **Imaging challenge**: single-element transducers provide high resolution only near their focal zone, with rapid degradation outside the nominal depth of field.\n\n- **SAFT principle**: the focal point is treated as a virtual source, and beam overlap during scanning is used to reconstruct a more uniform image over depth.\n\n- **Experimental validation**:\n  1. **2D validation**: aluminum-wire phantoms were used to quantify gains in depth of field and signal-to-noise ratio through PSF analysis.\n\n![image_saft](images/projects/image_saft.webp)\n\n  2. **3D validation**: a 3D printed wire-grid phantom scanned at 4 mm and 10 mm demonstrated improved sharpness with pseudo-3D SAFT.\n\n![saft_eq](images/projects/saft_eq.webp)\n\n- **Software tools**: MATLAB implementation and Viewer3D MIP visualization supported the validation workflow.\n\nThe project showed that SAFT can significantly improve image quality without adding complex electronics.',
        },
        acousticOutputTable: {
          title: 'Acoustic Output Table for Supersonic Imagine Mach Series Ultrasound Scanner',
          bodyText:
            '- This project developed an Acoustic Output Table (AOT) for Supersonic Imagine Mach scanners, covering safety indicators such as MI, PII, TIS, TIB, and TIC.\n\nKey aspects included:\n\n1. **Measurement process**:\n   - high-precision hydrophone measurements of acoustic pressure waveforms\n   - spatial and temporal mapping of outputs in water tanks\n\n2. **Parameter calculations**:\n   - MI for non-thermal risk assessment such as cavitation\n   - PII for energy delivered per unit area\n   - TIS, TIB, and TIC for soft-tissue, bone-interface, and cranial thermal evaluation\n\n3. **Safety compliance**:\n   - alignment with IEC 60601-2-37 and ALARA principles\n   - monitoring of MI and TI values for safe clinical operation\n\n4. **Reporting and visualization**:\n   - summary tables by imaging mode\n   - graphical representations including isocontour intensity maps\n\nThe project emphasized precise measurement and reporting as a basis for both safety and diagnostic performance.',
        },
        waverKGpuWorkbench: {
          title: 'Waver : Ultrasound Simulation Software',
          introText:
            '- Developed an ultrasound simulation software environment to study propagation, focused transmission, electronic steering, and beam quality from a single interface.\n- The software combines a Python GUI with accelerated numerical backends, making it practical for both R&D iteration and technical demonstration.',
          stackText:
            '**Code stack**\n- **Python** for the application layer and workflow orchestration.\n- **NiceGUI** for the interactive control panel.\n- **NumPy** and **Plotly** for numerical post-processing and beam visualization.\n- **CuPy** for GPU-accelerated k-space / PSTD propagation in the `kgpu` workflow.\n- **Rust** for the `waver_rust` core, with **PyO3** and **Maturin** used to expose compiled simulation code to Python.',
          bodyText:
            '**What the software can do**\n1. **Source modes**: simulate plane waves, pistons, focused pistons, and phased arrays.\n2. **Array options**: set element count, pitch, kerf, aperture, focus depth, and lateral steering targets.\n3. **Pulse control**: adjust center frequency, pulse length in cycles, and exported frame count.\n4. **Medium setup**: build layered media with presets such as water, soft tissue, cortical bone, stainless steel, aluminium, or custom materials.\n5. **Grid and boundaries**: tune domain size, spatial sampling, and PML absorbing boundaries.\n6. **Scatterer placement**: place scatterers manually anywhere in the propagation domain to create custom scattering scenarios.\n7. **Analysis**: inspect pressure or envelope animations, continuous-wave beam maps, FWHM estimates, impulse response plots, and export CSV, PNG, and MP4 outputs.\n\n![FWHM Analysis](images/projects/waver-array-fwhm.webp)\n\n**Future work**\n- Add heterogeneous tissue maps, scatterer libraries, and richer multilayer presets.\n- Extend transmit simulations toward receive beamforming and pulse-echo imaging.\n- Add automated sidelobe, focal shift, and steering-loss benchmarks.\n- Integrate optimization loops for array design and sequence tuning.',
          heroMedia: {
            caption: 'Plane Wave propagation in water with Scatterers.',
          },
          mediaAfterIntro: [
            { caption: 'Focused transmission pressure field.' },
            { caption: 'Beam map associated with the focused transmission setup.' },
          ],
          mediaAfterStack: [
            { caption: 'Steered transmission pressure field.' },
            { caption: 'Steered beam map showing off-axis focusing.' },
          ],
        },
        websiteDevelopment: {
          title: 'JR-AI-Consulting Website Development',
          bodyText:
            '- The JR-AI-Consulting website was built to present the company expertise in AI consulting, data analysis, and software services.\n- The site includes a clean professional structure with dedicated sections for services, portfolio, and contact.\n- The technical stack combines **React** on the frontend with **Node.js** services and a **MongoDB** backend.',
        },
      },
    },
    about: {
      content:
        '&nbsp; &nbsp; &nbsp; **JR-AI-Consulting** provides extensive expertise in ultrasound engineering, backed by a strong foundation in physics and more than a decade of experience in the ultrasound industry. The team has contributed across ultrasound transducer architecture, R&D development, senior ultrasound physics, and laboratory operations management.  \n\n&nbsp; &nbsp; &nbsp; With a strong track record in transducer design and development, JR-AI-Consulting works with advanced modeling techniques such as the KLM model and finite element analysis. The company has supported the creation of transducers for demanding medical and industrial applications, with designs optimized around the real constraints of each client.  \n\n&nbsp; &nbsp; &nbsp; The expertise also covers the development of advanced ultrasound imaging systems. By combining T/X analog electronics, CAD, 3D printing, acoustic material characterization, beam modeling, signal processing, and project management, JR-AI-Consulting has delivered innovative prototypes, including high-frequency imaging systems, for complex technical programs.  \n\n&nbsp; &nbsp; &nbsp; JR-AI-Consulting also offers specialized support in ultrasound physics, including system quality assurance and protocol development. This work has contributed to imaging performance validation and compliance alignment in collaboration with manufacturers and research centers.  \n\n&nbsp; &nbsp; &nbsp; Technical proficiency spans tools such as Matlab, COMSOL, PZFlex/Onscale, Linux, Python, C++, SolidWorks, and SQL. The company is also familiar with major medical standards including NEMA UD2, NEMA UD3, IEC 60601-2-37, IEC 62359, IEC 62127-1, ACT Accreditation, FDA (510k), ISO 13485, and ISO 9001.  \n\n&nbsp; &nbsp; &nbsp; Beyond ultrasound, **JR-AI-Consulting** also operates in **Scientific Consulting**, with expert guidance for research, technical problem solving, and high-level analysis. Its **Numerical Computation** work focuses on advanced simulation using tools such as Matlab, Python, and COMSOL. In **Web Design**, the company builds professional, responsive websites with frameworks like React, Next.js, and Bootstrap. It also delivers **Teaching & Training** programs in physics, mathematics, and ultrasound engineering.  \n\n&nbsp; &nbsp; &nbsp; At **JR-AI-Consulting**, the focus remains on innovation, rigor, and practical delivery. The combination of technical depth and product execution helps clients solve concrete problems and move ambitious projects forward.',
    },
    footer: {
      copyright: 'JR-AI-Consulting © 2024. All rights reserved. Developed by JR-AI-Consulting',
    },
    services: {
      noServices: 'No services available.',
      scientificConsulting: {
        title: 'Scientific Consulting',
        description:
          'Expert support for research strategy, technical problem solving, and high-level analysis aligned with client objectives.',
      },
      numericalComputation: {
        title: 'Numerical Computation',
        description:
          'Advanced modeling and simulation with Matlab, Python, COMSOL, and related tools for reliable technical decisions.',
      },
      webDesign: {
        title: 'Web Design',
        description:
          'Professional, responsive, and user-focused websites built with modern frameworks and a clear business goal.',
      },
      teachingTraining: {
        title: 'Teaching and Training',
        description:
          'Tailored training programs in physics, mathematics, ultrasound engineering, and technical upskilling.',
      },
      electronicInvoicing: {
        title: 'Electronic Invoicing',
        description:
          'Prepare your business for e-invoicing with invoice checks, missing-field detection, Factur-X generation, and guided rollout before PDP integration.',
        ctaLabel: 'Book a demo',
      },
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      services: 'Services',
      einvoicing: 'Facturation',
      projects: 'Projets',
      contact: 'Contact',
      about: 'À propos',
    },
    sections: {
      services: 'Services',
      projects: 'Projets',
      about: 'À propos',
      contact: 'Contact',
    },
    home: {
      roles: 'TROUVEZ VOTRE SOLUTION',
      description:
        'Conseil scientifique, développement web augmenté par l IA et services numériques ciblés pour entreprises et équipes techniques.',
    },
    spotlight: {
      eyebrow: 'Service complementaire',
      title: 'Accompagnement facture électronique',
      summary:
        'Une offre ciblée pour vérifier les données de facture, détecter les champs manquants, générer du Factur-X et préparer le déploiement avant une intégration PDP en production.',
      highlights: [
        'Controle de conformite facture',
        'Detection des champs manquants',
        'Generation Factur-X',
        'Accompagnement au deploiement',
      ],
      primary: 'Réserver une démo',
      secondary: 'Nous contacter',
      price: 'Offre pilote a partir de 490 EUR',
      panelTitle: 'Inclus dans ce service',
    },
    einvoicingPage: {
      eyebrow: 'Facturation numerique',
      title: 'Une page dediee pour expliquer, demontrer, tarifer et cadrer la facture electronique.',
      summary:
        'Cette offre vise les TPE, PME et equipes qui ont besoin d une page claire: explication du parcours, demo visuelle de facture, prix, points juridiques, et moyens de paiement comme Stripe ou le virement.',
      metrics: [
        { value: 'Factur-X', label: 'Sortie PDF/XML structuree' },
        { value: '30 min', label: 'Demo de cadrage type' },
        { value: 'PDP-ready', label: 'Prete pour la phase connecteur' },
      ],
      primary: 'Reserver une demo',
      secondary: 'Retour aux services',
      price: 'Pack pilote a partir de 490 EUR',
      panelTitle: 'Contenu de la page dediee',
      highlights: [
        'Explication claire du flux de facturation electronique',
        'Demo interactive avec calculs en direct',
        'Offres prix pour audit, deploiement et support',
        'Blocs juridique et paiement deja structurés',
      ],
      complianceTitle: 'Point de vigilance',
      complianceChips: ['Factur-X', 'Champs obligatoires', 'Conditions de paiement', 'Archivage'],
      explainerEyebrow: 'Explication',
      explainerTitle: 'Ce que la page doit couvrir',
      explainerCards: [
        {
          title: 'Clarifier le process',
          body: 'Montrer la difference entre un PDF classique, une facture structuree et le futur circuit d echange avec une plateforme.',
        },
        {
          title: 'Mieux vendre',
          body: 'Transformer une contrainte reglementaire en offre concrete avec prix visibles, livrables clairs et demo rassurante pour le prospect.',
        },
        {
          title: 'Preparer le deploiement',
          body: 'Lister les donnees a fiabiliser: identite emetteur, TVA, numerotation, moyens de paiement, statuts et futur routage PDP.',
        },
      ],
      demoEyebrow: 'Demo interactive',
      demoTitle: 'Apercu de facture en direct',
      demoSummary:
        'Les inputs ci-dessous alimentent une fausse facture, des marqueurs juridiques, des infos de paiement et des visuels generes reutilisables sur la page.',
      form: {
        issuer: 'Emetteur',
        client: 'Client',
        invoiceNumber: 'Numero de facture',
        amount: 'Montant HT',
        vatRate: 'TVA %',
        dueDays: 'Delai de paiement en jours',
        paymentMethod: 'Moyen de paiement',
        format: 'Format de sortie',
        notes: 'Note interne',
      },
      paymentMethods: [
        { value: 'stripe', label: 'Lien de paiement Stripe' },
        { value: 'transfer', label: 'Virement bancaire' },
        { value: 'sepa', label: 'Prelevement SEPA' },
        { value: 'card', label: 'Carte enregistree' },
      ],
      formats: [
        { value: 'factur-x', label: 'Factur-X' },
        { value: 'pdf-a3', label: 'PDF/A-3' },
        { value: 'ubl', label: 'UBL' },
        { value: 'cii', label: 'CII' },
      ],
      preview: {
        title: 'Apercu facture',
        client: 'Client',
        format: 'Format',
        payment: 'Paiement',
        subtotal: 'Sous-total',
        vat: 'TVA',
        total: 'Total a payer',
        tags: ['Donnees structurees', 'Pret pour validation', 'Statut paiement tracable'],
      },
      generatedImages: [
        {
          label: 'Visuel genere',
          title: 'Carte hero',
          body: 'Un visuel de page genere a partir des metadonnees de facture pour une landing ou une proposition commerciale.',
        },
        {
          label: 'Visuel genere',
          title: 'Tuile conformite',
          body: 'Une carte compacte pour afficher champs obligatoires, echeance et moyen de paiement choisi.',
        },
        {
          label: 'Visuel genere',
          title: 'Resume client',
          body: 'Un bloc reutilisable pour onboarding, emails ou transition vers un checkout Stripe.',
        },
      ],
      pricingEyebrow: 'Tarifs',
      pricingTitle: 'Trois niveaux d offre',
      pricingCards: [
        {
          title: 'Audit starter',
          price: '490 EUR',
          summary: 'Pour une entite qui veut un point de depart concret.',
          items: ['Revue d exemples de factures', 'Checklist des champs manquants', 'Mise en place de la page demo'],
        },
        {
          title: 'Sprint implementation',
          price: '1 490 EUR',
          summary: 'Pour une equipe qui veut la page, la demo et le cadrage du deploiement.',
          items: ['Design de la section dediee', 'Demo Factur-X', 'Cadrage des flux de paiement'],
          featured: true,
        },
        {
          title: 'Support mensuel',
          price: '290 EUR / mois',
          summary: 'Pour iterer pendant la montee en conformite.',
          items: ['Mises a jour de contenu', 'Ajout de blocs paiement', 'Suivi checklist pre-PDP'],
        },
      ],
      legalEyebrow: 'Juridique et conformite',
      legalTitle: 'Points juridiques a afficher',
      legalItems: [
        {
          title: 'Obligation d emission',
          body: 'Au 1er septembre 2026, les grandes entreprises et ETI en France doivent emettre des factures electroniques, tandis que toutes les entreprises doivent etre en mesure d en recevoir.',
        },
        {
          title: 'Calendrier PME',
          body: 'Au 1er septembre 2027, les PME, TPE et micro-entreprises doivent a leur tour rejoindre l obligation d emission, d ou l interet de se preparer en amont.',
        },
        {
          title: 'Perimetre',
          body: 'La page doit indiquer que les informations juridiques sont fournies a titre informatif, que la validation comptable reste cote client et que le routage final dependra de la plateforme retenue et du SI comptable.',
        },
      ],
      paymentEyebrow: 'Moyens de paiement',
      paymentTitle: 'Positionnement paiement',
      paymentSummary:
        'La page peut presenter un lien Stripe, le virement, le prelevement SEPA ou un flux carte deja enregistree. La structure est prevue pour montrer le parcours commercial avant meme l automatisation complete du paiement.',
      paymentBadges: ['Stripe', 'Virement', 'SEPA', 'Conditions de paiement', 'Webhook futur'],
    },
    contact: {
      title: 'Contact',
      firstName: 'Prenom',
      lastName: 'Nom',
      companyName: 'Entreprise',
      email: 'Email',
      phone: 'Telephone',
      message: 'Message',
      send: 'Envoyer',
      sending: 'Envoi...',
      success: 'Email envoye avec succes.',
      error: "Échec de l envoi. Merci de réessayer.",
    },
    projects: {
      showMore: 'Voir plus',
      noProjects: 'Aucun projet disponible.',
      noDescription: 'Aucune description disponible.',
      visitWebsite: 'Visiter le site',
      items: {
        topographicReconstruction: {
          title: 'Reconstruction topographique par ultrasons',
          bodyText:
            "- Ce projet a exploré la reconstruction topographique par ultrasons pour des applications dentaires comme l'empreinte numérique.\n- Un montage haute fréquence a été évalué comme alternative aux systèmes optiques et aux moules classiques dans des conditions perturbées par la salive, les fluides et l'inconfort patient.\n\nPoints clés:\n\n1. **Montage expérimental**: la reconstruction ultrasonore de surfaces dentaires a été comparée à des références optiques comme la microscopie confocale et les scanners intra-oraux.\n\n2. **Méthodes de reconstruction**: les approches Time of Flight (TOF) et Iterative Closest Point (ICP) ont été étudiées pour améliorer la précision géométrique.\n\n3. **Comparaison aux systèmes optiques**: les ultrasons apportent pénétration et robustesse dans des conditions difficiles, tandis que l'optique conserve une meilleure résolution de surface.\n\n4. **Traitement SAFT**: la Synthetic Aperture Focusing Technique a été utilisée pour étendre la profondeur de champ et stabiliser la résolution hors foyer.\n\nL'objectif était d'évaluer les ultrasons comme alternative crédible pour la capture de structures dentaires complexes avec une meilleure capacité en profondeur.",
        },
        printedPhantoms: {
          title: 'Fantomes imprimes 3D pour imagerie ultrasonore haute frequence',
          bodyText:
            "- Cette etude a evalue l'impression 3D par photopolymer jetting pour fabriquer des fantomes sur mesure destines a l'imagerie ultrasonore au-dela de 20 MHz.\n- Le travail repond au manque de fantomes commerciaux adaptes aux contraintes de la haute frequence.\n\nPoints cles:\n\n1. **Procede d'impression**: le photopolymer jetting permet des geometries precises et des structures controlees pour les essais acoustiques.\n\n2. **Caracterisation materiaux**: six photopolymeres ont ete mesures en impedance et attenuation pour approcher le comportement des tissus.\n\n3. **Evaluation acoustique**: un traducteur focalise 15 a 55 MHz et une chaine d'amplification dediee ont servi aux mesures.\n\n4. **Performance d'imagerie**: les fantomes ont montre un comportement acoustique realiste et des rapports signal sur bruit allant jusqu'a 50 dB.\n\nL'etude montre que l'impression 3D constitue une voie rapide et pratique pour les outils de calibration et de validation en ultrasons haute frequence.",
        },
        piezoelectricTransducer: {
          title: "Conception et fabrication d'un transducteur piezoelectrique",
          bodyText:
            "- Cette recherche portait sur un transducteur mono-element base sur le copolymere piezoelectrique P(VDF-TrFE) pour l'ultrason haute frequence.\n- La cible etait une sonde intra-orale avec une resonance proche de 40 MHz et un diametre actif de 4 mm.\n\nPoints cles:\n\n1. **Modelisation et simulation**: la modelisation KLM et l'analyse par elements finis ont servi a predire les performances et optimiser l'empilement.\n\n![bandWidth](images/projects/BW.webp)\n\n2. **Choix materiau**: le P(VDF-TrFE) a ete retenu pour sa plus faible impedance acoustique, sa flexibilite et sa resilience mecanique.\n\n3. **Fabrication**: l'assemblage combine pressage, dos 3D imprimes et collage epoxy.\n\n4. **Validation**: les resultats d'imagerie sont restes comparables a des references commerciales sur tissus mous, avec une sensibilite plus faible sous 2 mm.\n\n![Transducer Image](images/projects/3d_us_image_mono.webp)\n\nLe travail valide l'interet de transducteurs sur mesure pour une imagerie intra-orale compacte a haute frequence.",
        },
        annularArray: {
          title: "Evaluation d'un reseau annulaire P(VDF-TrFE) sur silicium",
          bodyText:
            "- Cette étude a porté sur des dispositifs ultrasonores haute fréquence basés sur du P(VDF-TrFE) déposé sur wafers silicium.\n\n- **Conception et fabrication**: des couches spin-coatées ont été patternisées en réseaux annulaires à électrodes concentriques pour limiter le couplage et améliorer la réponse acoustique.\n\n- **Caractérisation**: la réponse électro-acoustique et les profils vibratoires ont été mesurés par interférométrie laser et analyse d'impédance.\n\n![Transducer Image](images/projects/Annu_spectro.webp)\n\n- **Modélisation**: des simulations DREAM ont servi à estimer les champs de pression dans l'eau autour de 40 MHz pour plusieurs focales.\n\n- **Perspectives**: l'étude a mis en avant des pistes sur la polarisation et la composition matériau pour augmenter les performances de systèmes ultrasonores intégrés.",
        },
        probeEfficiency: {
          title:
            "Mesures acoustiques et electriques d'une sonde medicale phased array pour calcul de rendement",
          bodyText:
            "- Ce projet a etudie l'estimation du rendement d'une sonde medicale phased array au moyen de mesures acoustiques et electriques combinees.\n\n- **Mesures acoustiques**: des scans azimut-elevation ont ete realises au foyer geometrique avec un seul element actif. Un hydrophone a membrane a capture 100 x 240 formes d'onde sur une zone de 24 x 10 mm, filtrees entre 3 et 13 MHz.\n\n![PII](images/projects/PII_xy.webp)\n\n- **Mesures electriques**: les donnees d'impedance et de tension ont servi a evaluer la calibration et la fidelite du signal.\n\n- **Calcul de rendement**: des rapports de densite spectrale d'energie ont compare l'energie acoustique emise a la puissance electrique d'entree.\n\n- **Recommandations**: l'etude propose des ameliorations sur l'echantillonnage, l'averaging et l'analyse spectrale des impulsions.\n\nCe travail souligne l'importance d'une caracterisation electro-acoustique rigoureuse pour optimiser les sondes cliniques.",
        },
        saftAlgorithm: {
          title: 'Algorithme SAFT pour augmenter la profondeur de champ en imagerie ultrasonore',
          bodyText:
            "- Ce projet a vise l'amelioration de la profondeur de champ en imagerie ultrasonore via la Synthetic Aperture Focusing Technique (SAFT).\n\n- **Probleme d'imagerie**: un transducteur mono-element offre une forte resolution surtout dans sa zone focale, avec une degradation rapide hors profondeur nominale.\n\n- **Principe SAFT**: le point focal est traite comme une source virtuelle et le recouvrement des faisceaux pendant le scan permet de reconstruire une image plus uniforme en profondeur.\n\n- **Validation experimentale**:\n  1. **Validation 2D**: des fantomes a fils d'aluminium ont permis de quantifier les gains en profondeur de champ et en rapport signal sur bruit via l'analyse de PSF.\n\n![image_saft](images/projects/image_saft.webp)\n\n  2. **Validation 3D**: un fantome grillage imprime 3D scanne a 4 mm et 10 mm a montre une meilleure nettete avec le pseudo-3D SAFT.\n\n![saft_eq](images/projects/saft_eq.webp)\n\n- **Outils logiciels**: l'implementation MATLAB et la visualisation Viewer3D en mode MIP ont soutenu la validation.\n\nLe projet montre que le SAFT peut fortement ameliorer la qualite d'image sans ajouter d'electronique complexe.",
        },
        acousticOutputTable: {
          title: 'Table de sortie acoustique pour scanner ultrasonore Supersonic Imagine Mach',
          bodyText:
            "- Ce projet a consisté à développer une Acoustic Output Table (AOT) pour les scanners Supersonic Imagine Mach, couvrant des indicateurs de sécurité tels que MI, PII, TIS, TIB et TIC.\n\nPoints clés:\n\n1. **Processus de mesure**:\n   - mesures hydrophone de haute précision des ondes de pression acoustique\n   - cartographie spatiale et temporelle des sorties en cuve à eau\n\n2. **Calcul des paramètres**:\n   - MI pour l'évaluation des risques non thermiques comme la cavitation\n   - PII pour l'énergie délivrée par unité de surface\n   - TIS, TIB et TIC pour l'évaluation thermique tissus mous, interface osseuse et os crânien\n\n3. **Conformité sécurité**:\n   - alignement avec l'IEC 60601-2-37 et les principes ALARA\n   - suivi des valeurs MI et TI pour un usage clinique sûr.\n\n4. **Reporting et visualisation**:\n   - tableaux de synthèse par mode d'imagerie\n   - représentations graphiques dont des cartes d'isocontours d'intensité\n\nLe projet souligne que la précision des mesures et du reporting conditionne à la fois sécurité et performance diagnostique.",
        },
        waverKGpuWorkbench: {
          title: 'Waver : Ultrasound Simulation Software',
          introText:
            "- Développement d'un environnement logiciel de simulation ultrasonore pour étudier la propagation, l'émission focalisée, le steering électronique et la qualité de faisceau depuis une seule interface.\n- Le logiciel combine une interface Python et des backends numériques accélérés, ce qui le rend utile pour l'itération R&D comme pour la démonstration technique.",
          stackText:
            "**Stack code**\n- **Python** pour la couche applicative et l'orchestration du workflow.\n- **NiceGUI** pour le panneau de contrôle interactif.\n- **NumPy** et **Plotly** pour le post-traitement numérique et la visualisation des faisceaux.\n- **CuPy** pour la propagation k-space / PSTD accélérée sur GPU dans le workflow `kgpu`.\n- **Rust** pour le coeur `waver_rust`, avec **PyO3** et **Maturin** pour exposer le code compilé à Python.",
          bodyText:
            "**Ce que le logiciel permet**\n1. **Modes source** : simuler des ondes planes, pistons, pistons focalisés et phased arrays.\n2. **Options réseau** : régler le nombre d'éléments, le pitch, le kerf, l'ouverture, la profondeur de foyer et les cibles latérales de steering.\n3. **Contrôle impulsion** : ajuster la fréquence centrale, la longueur d'impulsion en cycles et le nombre d'images exportées.\n4. **Configuration du milieu** : construire des milieux multicouches avec des presets eau, tissu mou, os cortical, acier inoxydable, aluminium ou matériaux personnalisés.\n5. **Grille et bords** : régler la taille du domaine, l'échantillonnage spatial et les bords absorbants PML.\n6. **Placement de diffuseurs** : placer manuellement des diffuseurs n'importe où dans le domaine de propagation pour créer des scénarios de diffusion sur mesure.\n7. **Analyse** : visualiser animations de pression ou d'enveloppe, beam maps CW, estimations FWHM, réponses impulsionnelles, export CSV, PNG et MP4.\n\n![Analyse FWHM](images/projects/waver-array-fwhm.webp)\n\n**Travaux futurs**\n- Ajouter des cartes tissulaires hétérogènes, des bibliothèques de diffuseurs et des presets multicouches plus riches.\n- Étendre les simulations d'émission vers le beamforming en réception et l'imagerie pulse-écho.\n- Ajouter des benchmarks automatiques sur les lobes secondaires, le décalage focal et la perte liée au steering.\n- Intégrer des boucles d'optimisation pour le design des réseaux et le réglage des séquences.",
          heroMedia: {
            caption: 'Plane Wave propagation in water with Scatterers.',
          },
          mediaAfterIntro: [
            { caption: "Champ de pression en émission focalisée." },
            { caption: "Beam map associée à la configuration d'émission focalisée." },
          ],
          mediaAfterStack: [
            { caption: 'Champ de pression en émission steerée.' },
            { caption: 'Beam map steerée montrant la focalisation hors axe.' },
          ],
        },
        websiteDevelopment: {
          title: 'Developpement du site JR-AI-Consulting',
          bodyText:
            "- Le site JR-AI-Consulting a ete concu pour presenter l'expertise de l'entreprise en conseil IA, analyse de donnees et services logiciels.\n- Le site propose une structure professionnelle avec des sections dediees aux services, au portfolio et au contact.\n- La stack technique combine **React** pour le frontend avec des services **Node.js** et un backend **MongoDB**.",
        },
      },
    },
    about: {
      content:
        '&nbsp; &nbsp; &nbsp; **JR-AI-Consulting** apporte une expertise étendue en ingénierie ultrasonore, appuyée sur une base solide en physique et plus de dix années d expérience dans l industrie des ultrasons. L équipe est intervenue sur des sujets variés: architecture de transducteurs ultrasonores, développement R&D, physique ultrasonore de niveau senior et gestion d activités de laboratoire.  \n\n&nbsp; &nbsp; &nbsp; Avec un historique solide en conception et développement de transducteurs, JR-AI-Consulting travaille avec des techniques avancées de modélisation comme le modèle KLM et l analyse par éléments finis. L entreprise a accompagné la création de transducteurs pour des applications médicales et industrielles exigeantes, avec des conceptions optimisées autour des contraintes réelles des clients.  \n\n&nbsp; &nbsp; &nbsp; L expertise couvre aussi le développement de systèmes d imagerie ultrasonore avancés. En combinant électronique analogique T/X, CAO, impression 3D, caractérisation de matériaux acoustiques, modélisation de faisceaux, traitement du signal et gestion de projet, JR-AI-Consulting a livré des prototypes innovants, notamment en imagerie haute fréquence, pour des programmes techniques complexes.  \n\n&nbsp; &nbsp; &nbsp; JR-AI-Consulting propose également un accompagnement spécialisé en physique ultrasonore, incluant assurance qualité système et développement de protocoles. Ce travail a contribué à la validation des performances d imagerie et à l alignement réglementaire en collaboration avec des fabricants et des centres de recherche.  \n\n&nbsp; &nbsp; &nbsp; La maîtrise technique couvre des outils comme Matlab, COMSOL, PZFlex/Onscale, Linux, Python, C++, SolidWorks et SQL. L entreprise connaît aussi les principales normes médicales, notamment NEMA UD2, NEMA UD3, IEC 60601-2-37, IEC 62359, IEC 62127-1, ACT Accreditation, FDA (510k), ISO 13485 et ISO 9001.  \n\n&nbsp; &nbsp; &nbsp; Au-delà des ultrasons, **JR-AI-Consulting** intervient aussi en **Conseil scientifique**, avec un accompagnement expert pour la recherche, la résolution de problèmes techniques et les analyses de haut niveau. Son activité en **Calcul numérique** s appuie sur des simulations avancées avec Matlab, Python et COMSOL. En **Conception web**, l entreprise conçoit des sites professionnels et responsives avec des frameworks comme React, Next.js et Bootstrap. Elle propose aussi des programmes de **Formation** en physique, mathématiques et ingénierie ultrasonore.  \n\n&nbsp; &nbsp; &nbsp; Chez **JR-AI-Consulting**, l objectif reste l innovation, la rigueur et la livraison concrète. Cette combinaison entre profondeur technique et exécution produit permet d aider les clients à résoudre des problèmes réels et à faire avancer des projets ambitieux.',
    },
    footer: {
      copyright: 'JR-AI-Consulting © 2024. Tous droits reserves. Developpe par JR-AI-Consulting',
    },
    services: {
      noServices: 'Aucun service disponible.',
      scientificConsulting: {
        title: 'Conseil scientifique',
        description:
          'Accompagnement expert pour la strategie de recherche, la resolution de problemes techniques et les analyses de haut niveau.',
      },
      numericalComputation: {
        title: 'Calcul numerique',
        description:
          'Modelisation et simulation avancees avec Matlab, Python, COMSOL et des outils adaptes aux decisions techniques fiables.',
      },
      webDesign: {
        title: 'Conception web',
        description:
          'Sites web professionnels, responsives et centres utilisateur, construits avec des frameworks modernes et un objectif business clair.',
      },
      teachingTraining: {
        title: 'Formation',
        description:
          'Programmes de formation sur mesure en physique, mathematiques, ingenierie ultrasonore et montee en competence technique.',
      },
      electronicInvoicing: {
        title: 'Facture electronique',
        description:
          'Préparez votre entreprise à la facture électronique avec contrôle des factures, détection des champs manquants, génération Factur-X et accompagnement avant intégration PDP.',
        ctaLabel: 'Réserver une démo',
      },
    },
  },
  zh: {
    nav: {
      home: '首页',
      services: '服务',
      einvoicing: '电子开票',
      projects: '项目',
      contact: '联系',
      about: '关于',
    },
    sections: {
      services: '服务',
      projects: '项目',
      about: '关于',
      contact: '联系',
    },
    home: {
      roles: '找到适合您的方案',
      description:
        '面向企业与技术团队的科学咨询、AI 增强型网站开发，以及精选数字化服务。',
    },
    spotlight: {
      eyebrow: '附加服务',
      title: '电子发票支持',
      summary:
        '帮助您检查发票数据、识别缺失的合规字段、生成 Factur-X 文件，并在接入实时 PDP 之前规划上线流程。',
      highlights: [
        '发票合规检查',
        '缺失字段识别',
        'Factur-X 生成',
        '上线辅导',
      ],
      primary: '预约演示',
      secondary: '联系我们',
      price: '试点服务 490 欧元起',
      panelTitle: '服务包含',
    },
    contact: {
      title: '联系',
      firstName: '名字',
      lastName: '姓氏',
      companyName: '公司',
      email: '邮箱',
      phone: '电话',
      message: '留言',
      send: '发送',
      sending: '发送中...',
      success: '邮件发送成功。',
      error: '发送失败，请重试。',
    },
    projects: {
      showMore: '查看更多',
      noProjects: '暂无项目。',
      noDescription: '暂无描述。',
      visitWebsite: '访问网站',
      items: {
        topographicReconstruction: {
          title: '超声地形重建',
          bodyText:
            '- 本项目研究将超声技术用于牙科等场景的表面地形重建，例如数字化取模。\n- 我们评估了一套高频实验系统，作为光学系统和传统模具之外的替代方案，以应对口腔液体干扰和患者不适等问题。\n\n关键内容包括：\n\n1. **实验系统**：将牙面超声重建方案与共聚焦显微镜、口内扫描仪等光学参考方案进行比较。\n\n2. **重建方法**：评估了飞行时间（TOF）和 ICP 等算法，以提高几何精度。\n\n3. **与光学系统比较**：超声在复杂条件下更稳健并具备穿透能力，而光学在表面分辨率上通常更强。\n\n4. **SAFT 处理**：采用合成孔径聚焦技术扩展景深，并改善焦区外的分辨率稳定性。\n\n项目目标是验证超声是否能够成为复杂牙科表面采集的有效替代方案，并提供更好的深度能力。',
        },
        printedPhantoms: {
          title: '用于高频超声成像的 3D 打印体模',
          bodyText:
            '- 本研究评估了光敏聚合物喷射 3D 打印在 20 MHz 以上超声成像体模制造中的应用。\n- 研究动因在于现有商用体模往往无法满足高频成像要求。\n\n关键内容包括：\n\n1. **3D 打印工艺**：光敏喷射可实现高精度几何结构和受控内部构型，适合声学测试。\n\n2. **材料表征**：对六种光敏材料的声阻抗和衰减进行测量，以逼近组织特性。\n\n3. **声学评估**：使用 15 至 55 MHz 聚焦换能器和专用放大链路开展测量。\n\n4. **成像性能**：体模呈现出较真实的声学行为，信噪比最高可达 50 dB。\n\n研究表明，3D 打印是构建高频超声标定与验证工具的一条快速而实用的路径。',
        },
        piezoelectricTransducer: {
          title: '压电换能器的设计与制造',
          bodyText:
            '- 本研究围绕基于 P(VDF-TrFE) 压电共聚物的单元换能器，面向高频超声应用展开。\n- 目标是实现一支用于口内成像的探头，谐振频率约 40 MHz，主动口径 4 mm。\n\n关键内容包括：\n\n1. **建模与仿真**：通过 KLM 等效电路和有限元分析预测性能并优化结构堆栈。\n\n![bandWidth](images/projects/BW.webp)\n\n2. **材料选择**：P(VDF-TrFE) 因其较低声阻抗、柔性和机械韧性而被选用。\n\n3. **制造工艺**：采用压制、3D 打印背衬及环氧装配工艺完成换能器制造。\n\n4. **性能验证**：在软组织成像上结果接近商用品，但对小于 2 mm 目标的灵敏度有所下降。\n\n![Transducer Image](images/projects/3d_us_image_mono.webp)\n\n该研究验证了定制换能器在紧凑型高频口内成像中的可行性。',
        },
        annularArray: {
          title: '硅基 P(VDF-TrFE) 环形阵列评估',
          bodyText:
            '- 本研究考察了沉积于硅片上的 P(VDF-TrFE) 高频超声器件。\n\n- **设计与制造**：通过旋涂形成共聚物层，并图形化为同心电极的环形阵列，以降低单元间耦合并优化声学响应。\n\n- **表征**：利用激光干涉和阻抗分析测量电声响应与振动特性。\n\n![Transducer Image](images/projects/Annu_spectro.webp)\n\n- **建模**：使用 DREAM 软件模拟约 40 MHz 条件下不同焦距的水中声压场。\n\n- **后续方向**：研究指出可通过改进极化流程和材料配方来进一步提升集成超声系统性能。',
        },
        probeEfficiency: {
          title: '用于效率计算的相控阵超声医疗探头声学与电学测量',
          bodyText:
            '- 本项目通过联合声学与电学测量研究相控阵医疗探头的效率估算。\n\n- **声学测量**：在几何焦点处激励单个阵元，使用膜式水听器在 24 x 10 mm 区域采集 100 x 240 组波形，并在 3 至 13 MHz 范围内滤波处理。\n\n![PII](images/projects/PII_xy.webp)\n\n- **电学测量**：采集阻抗与电压数据，用于评估校准质量和信号保真度。\n\n- **效率计算**：采用能量谱密度比，将发射的声能与输入电功率进行比较。\n\n- **建议**：研究建议优化采样、平均次数和脉冲谱分析方法，以提升效率测量质量。\n\n该工作强调了严格电声表征对临床探头优化的重要性。',
        },
        saftAlgorithm: {
          title: '用于提升超声成像景深的 SAFT 算法',
          bodyText:
            '- 本项目利用合成孔径聚焦技术（SAFT）提升超声成像的景深。\n\n- **成像挑战**：单元换能器通常只在焦区附近具有高分辨率，离开标称景深后图像质量快速下降。\n\n- **SAFT 原理**：将焦点视作虚拟声源，利用扫描过程中的波束重叠来重建在更大深度范围内更均匀的图像。\n\n- **实验验证**：\n  1. **二维验证**：采用铝丝体模，通过 PSF 分析量化景深和信噪比的提升。\n\n![image_saft](images/projects/image_saft.webp)\n\n  2. **三维验证**：在 4 mm 和 10 mm 距离下扫描 3D 打印丝网体模，伪 3D SAFT 显示出更好的清晰度。\n\n![saft_eq](images/projects/saft_eq.webp)\n\n- **软件工具**：算法在 MATLAB 中实现，并结合 Viewer3D 的 MIP 显示完成验证。\n\n项目表明，SAFT 能在不增加复杂电子硬件的情况下显著提升成像质量。',
        },
        acousticOutputTable: {
          title: 'Supersonic Imagine Mach 系列超声扫描仪声输出表',
          bodyText:
            '- 本项目为 Supersonic Imagine Mach 系列扫描仪建立声输出表（AOT），覆盖 MI、PII、TIS、TIB、TIC 等安全指标。\n\n关键内容包括：\n\n1. **测量流程**：\n   - 使用高精度水听器测量声压波形\n   - 在水槽中获取声输出的时空分布\n\n2. **参数计算**：\n   - MI 用于评估空化等非热风险\n   - PII 用于量化单位面积脉冲能量\n   - TIS、TIB、TIC 分别用于软组织、骨界面和颅骨热评估\n\n3. **安全合规**：\n   - 对齐 IEC 60601-2-37 和 ALARA 原则\n   - 在临床使用中监控 MI 和 TI 值以确保安全\n\n4. **报告与可视化**：\n   - 按成像模式输出汇总表\n   - 使用等值线图等图形展示声强分布\n\n该项目强调，精确测量与清晰报告是兼顾安全性与诊断性能的基础。',
        },
        waverKGpuWorkbench: {
          title: 'Waver : Ultrasound Simulation Software',
          introText:
            '- 该软件环境用于研究超声传播、聚焦发射、电子偏转以及波束质量，所有功能集中在同一界面中。\n- 软件结合 Python 图形界面与加速数值后端，适合研发迭代和技术演示。',
          stackText:
            '**代码栈**\n- **Python**：应用层与流程编排。\n- **NiceGUI**：交互式控制面板。\n- **NumPy** 与 **Plotly**：数值后处理与波束可视化。\n- **CuPy**：在 `kgpu` 流程中执行 GPU 加速的 k-space / PSTD 传播。\n- **Rust**：提供 `waver_rust` 核心，并通过 **PyO3** 与 **Maturin** 暴露给 Python。',
          bodyText:
            '**软件能力**\n1. **声源模式**：支持平面波、活塞、聚焦活塞和相控阵。\n2. **阵列参数**：可设置阵元数、pitch、kerf、孔径、焦深以及电子偏转目标。\n3. **脉冲控制**：可调中心频率、脉冲周期数和导出帧数。\n4. **介质配置**：支持水、软组织、皮质骨、不锈钢、铝及自定义材料的分层介质。\n5. **网格与边界**：可调计算域尺寸、空间采样和 PML 吸收边界。\n6. **散射体放置**：可在传播域内任意位置手动放置散射体，构建自定义散射场景。\n7. **分析**：支持压力或包络动画、连续波波束图、FWHM 估计、脉冲响应图，以及 CSV、PNG、MP4 导出。\n\n![FWHM 分析](images/projects/waver-array-fwhm.webp)\n\n**后续工作**\n- 增加异质组织图、散射体库和更丰富的多层介质预设。\n- 从发射仿真扩展到接收波束形成与脉冲回波成像。\n- 增加旁瓣、焦点偏移和偏转损失的自动化基准评估。\n- 集成阵列设计与序列调优的优化循环。',
          heroMedia: {
            caption: 'Plane Wave propagation in water with Scatterers.',
          },
          mediaAfterIntro: [
            { caption: '聚焦发射的压力场。' },
            { caption: '与聚焦发射配置对应的波束图。' },
          ],
          mediaAfterStack: [
            { caption: '偏转发射的压力场。' },
            { caption: '展示离轴聚焦的偏转波束图。' },
          ],
        },
        websiteDevelopment: {
          title: 'JR-AI-Consulting 网站开发',
          bodyText:
            '- JR-AI-Consulting 网站用于展示公司在 AI 咨询、数据分析和软件服务方面的能力。\n- 网站采用清晰专业的结构，包含服务、作品集和联系信息等部分。\n- 技术栈包括前端 **React**、服务端 **Node.js** 以及 **MongoDB** 后端。',
        },
      },
    },
    about: {
      content:
        '&nbsp; &nbsp; &nbsp; **JR-AI-Consulting** 在超声工程领域拥有深厚经验，建立在扎实的物理学基础和十多年超声行业实践之上。团队曾参与超声换能器架构、研发开发、高级超声物理以及实验室运营管理等多类工作。  \n\n&nbsp; &nbsp; &nbsp; 在换能器设计与开发方面，JR-AI-Consulting 具备成熟经验，熟悉 KLM 模型、有限元分析等高级建模方法，并已支持多项医疗与工业高性能换能器项目，根据客户真实约束完成优化设计。  \n\n&nbsp; &nbsp; &nbsp; 团队能力也覆盖先进超声成像系统开发。通过结合 T/X 模拟电子设计、CAD、3D 打印、声学材料表征、声束建模、信号处理与项目管理，JR-AI-Consulting 已交付多项创新原型，包括高频成像系统。  \n\n&nbsp; &nbsp; &nbsp; JR-AI-Consulting 同时提供超声物理相关的专业服务，包括系统质量保证与协议开发，并曾与制造商和研究中心合作，参与成像性能验证与合规工作。  \n\n&nbsp; &nbsp; &nbsp; 技术能力覆盖 Matlab、COMSOL、PZFlex/Onscale、Linux、Python、C++、SolidWorks 和 SQL 等工具，也熟悉 NEMA UD2、NEMA UD3、IEC 60601-2-37、IEC 62359、IEC 62127-1、ACT Accreditation、FDA (510k)、ISO 13485 和 ISO 9001 等主要医疗标准。  \n\n&nbsp; &nbsp; &nbsp; 除超声领域外，**JR-AI-Consulting** 也提供 **科学咨询**、**数值计算**、**网站设计** 与 **培训教学** 服务，面向科研、技术难题求解、高级仿真、企业网站建设，以及物理、数学和超声工程培训等场景。  \n\n&nbsp; &nbsp; &nbsp; 对 **JR-AI-Consulting** 来说，核心始终是创新、严谨和可落地交付。技术深度与产品执行力的结合，使团队能够帮助客户解决实际问题，并推动高要求项目持续前进。',
    },
    footer: {
      copyright: 'JR-AI-Consulting © 2024。保留所有权利。由 JR-AI-Consulting 开发',
    },
    services: {
      noServices: '暂无服务。',
      scientificConsulting: {
        title: '科学咨询',
        description:
          '围绕研究策略、技术问题解决和高水平分析提供专家支持，贴合客户目标。',
      },
      numericalComputation: {
        title: '数值计算',
        description:
          '借助 Matlab、Python、COMSOL 等工具开展高级建模与仿真，支撑可靠的技术决策。',
      },
      webDesign: {
        title: '网站设计',
        description:
          '使用现代框架打造专业、响应式、以用户为中心并面向业务目标的网站。',
      },
      teachingTraining: {
        title: '培训教学',
        description:
          '提供物理、数学、超声工程与技术提升方向的定制培训方案。',
      },
      electronicInvoicing: {
        title: '电子发票',
        description:
          '通过发票检查、缺失字段识别、Factur-X 生成和上线辅导，帮助企业为电子发票改革做好准备。',
        ctaLabel: '预约演示',
      },
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      einvoicing: 'Facturacion',
      projects: 'Proyectos',
      contact: 'Contacto',
      about: 'Acerca de',
    },
    sections: {
      services: 'Servicios',
      projects: 'Proyectos',
      about: 'Acerca de',
      contact: 'Contacto',
    },
    home: {
      roles: 'ENCUENTRA TU SOLUCION',
      description:
        'Consultoría científica, desarrollo web aumentado con IA y servicios digitales selectivos para empresas y equipos técnicos.',
    },
    spotlight: {
      eyebrow: 'Servicio adicional',
      title: 'Soporte para factura electrónica',
      summary:
        'Una oferta enfocada para revisar datos de facturas, detectar campos obligatorios faltantes, generar Factur-X y preparar el despliegue antes de una integracion PDP en produccion.',
      highlights: [
        'Revision de conformidad',
        'Deteccion de campos faltantes',
        'Generacion Factur-X',
        'Acompanamiento de despliegue',
      ],
      primary: 'Reservar una demo',
      secondary: 'Contactarnos',
      price: 'Oferta piloto desde 490 EUR',
      panelTitle: 'Incluido en este servicio',
    },
    contact: {
      title: 'Contacto',
      firstName: 'Nombre',
      lastName: 'Apellido',
      companyName: 'Empresa',
      email: 'Email',
      phone: 'Telefono',
      message: 'Mensaje',
      send: 'Enviar',
      sending: 'Enviando...',
      success: 'Correo enviado correctamente.',
      error: 'No se pudo enviar. Intentalo de nuevo.',
    },
    projects: {
      showMore: 'Ver más',
      noProjects: 'No hay proyectos disponibles.',
      noDescription: 'No hay descripcion disponible.',
      visitWebsite: 'Visitar sitio web',
      items: {
        topographicReconstruction: {
          title: 'Reconstruccion topografica por ultrasonidos',
          bodyText:
            '- Este proyecto exploro la reconstruccion topografica mediante ultrasonidos para aplicaciones dentales como la toma de impresiones digitales.\n- Se evaluo un montaje de alta frecuencia como alternativa a los sistemas opticos y a los moldes tradicionales en entornos con saliva, fluidos e incomodidad del paciente.\n\nAspectos clave:\n\n1. **Montaje experimental**: la reconstruccion ultrasonica de superficies dentales se comparo con referencias opticas como microscopia confocal y escaneres intraorales.\n\n2. **Metodos de reconstruccion**: se evaluaron Time of Flight (TOF) e ICP para mejorar la precision geometrica.\n\n3. **Comparacion con sistemas opticos**: los ultrasonidos ofrecieron mayor robustez y penetracion en condiciones complejas, mientras que la optica mantuvo mejor resolucion superficial.\n\n4. **Procesado SAFT**: se utilizo la tecnica Synthetic Aperture Focusing Technique para ampliar la profundidad de campo y estabilizar la resolucion fuera del foco.\n\nEl objetivo fue validar los ultrasonidos como alternativa creible para captar estructuras dentales complejas con mejor capacidad en profundidad.',
        },
        printedPhantoms: {
          title: 'Fantomas impresos en 3D para imagen ultrasónica de alta frecuencia',
          bodyText:
            '- Este estudio evaluó la impresión 3D por photopolymer jetting para fabricar fantomas personalizados destinados a imagen ultrasónica por encima de 20 MHz.\n- El trabajo responde a la falta de fantomas comerciales adaptados a la alta frecuencia.\n\nAspectos clave:\n\n1. **Proceso de impresión**: el photopolymer jetting permitió geometrías precisas y estructuras controladas para ensayos acústicos.\n\n2. **Caracterización de materiales**: se midieron seis fotopolímeros en impedancia y atenuación para aproximar el comportamiento tisular.\n\n3. **Evaluación acústica**: se utilizaron un transductor focalizado de 15 a 55 MHz y una cadena de amplificación dedicada.\n\n4. **Prestaciones de imagen**: los fantomas mostraron comportamiento acústico realista y relaciones señal-ruido de hasta 50 dB.\n\nEl estudio demuestra que la impresión 3D es una vía rápida y práctica para herramientas de calibración y validación en ultrasonidos de alta frecuencia.',
        },
        piezoelectricTransducer: {
          title: 'Diseno y fabricacion de un transductor piezoelectrico',
          bodyText:
            '- Esta investigacion se centro en un transductor mono-elemento basado en el copolimero piezoelectrico P(VDF-TrFE) para ultrasonidos de alta frecuencia.\n- El objetivo era una sonda intraoral con resonancia cercana a 40 MHz y diametro activo de 4 mm.\n\nAspectos clave:\n\n1. **Modelado y simulacion**: el modelo KLM y el analisis por elementos finitos sirvieron para predecir prestaciones y optimizar la pila.\n\n![bandWidth](images/projects/BW.webp)\n\n2. **Seleccion de material**: se eligio P(VDF-TrFE) por su menor impedancia acustica, flexibilidad y resiliencia mecanica.\n\n3. **Fabricacion**: el ensamblaje combino prensado, respaldos impresos en 3D y adhesivo epoxi.\n\n4. **Validacion**: los resultados en tejidos blandos fueron comparables a referencias comerciales, con menor sensibilidad por debajo de 2 mm.\n\n![Transducer Image](images/projects/3d_us_image_mono.webp)\n\nEl trabajo demuestra la viabilidad de transductores a medida para imagen intraoral compacta de alta frecuencia.',
        },
        annularArray: {
          title: 'Evaluacion de un arreglo anular P(VDF-TrFE) sobre silicio',
          bodyText:
            '- Este estudio investigó dispositivos ultrasónicos de alta frecuencia basados en P(VDF-TrFE) depositado sobre obleas de silicio.\n\n- **Diseño y fabricación**: capas spin-coated se estructuraron en arreglos anulares con electrodos concéntricos para reducir el acoplamiento y mejorar la respuesta acústica.\n\n- **Caracterización**: la respuesta electroacústica y los perfiles vibratorios se midieron con interferometría láser y análisis de impedancia.\n\n![Transducer Image](images/projects/Annu_spectro.webp)\n\n- **Modelado**: simulaciones DREAM estimaron campos de presión en agua alrededor de 40 MHz para varias distancias focales.\n\n- **Perspectivas**: el estudio identificó mejoras en polarización y composición del material para elevar el rendimiento de sistemas ultrasónicos integrados.',
        },
        probeEfficiency: {
          title:
            'Medición acústica y eléctrica de una sonda médica phased array para calcular eficiencia',
          bodyText:
            '- Este proyecto analizó la estimación de eficiencia de una sonda médica phased array mediante mediciones acústicas y eléctricas combinadas.\n\n- **Mediciones acústicas**: se realizaron barridos azimut-elevación en el foco geométrico con un solo elemento activo. Un hidrófono de membrana capturó 100 x 240 formas de onda sobre una zona de 24 x 10 mm, filtradas entre 3 y 13 MHz.\n\n![PII](images/projects/PII_xy.webp)\n\n- **Mediciones eléctricas**: los datos de impedancia y voltaje permitieron evaluar calibración y fidelidad de señal.\n\n- **Cálculo de eficiencia**: se usaron relaciones de densidad espectral de energía para comparar la energía acústica emitida con la potencia eléctrica de entrada.\n\n- **Recomendaciones**: se propusieron mejoras en muestreo, promediado y análisis espectral para medidas pulsadas.\n\nEste trabajo subraya la importancia de una caracterización electroacústica rigurosa para optimizar sondas clínicas.',
        },
        saftAlgorithm: {
          title: 'Algoritmo SAFT para mejorar la profundidad de campo en imagen ultrasonica',
          bodyText:
            '- Este proyecto buscó mejorar la profundidad de campo en imagen ultrasónica mediante Synthetic Aperture Focusing Technique (SAFT).\n\n- **Problema de imagen**: un transductor mono-elemento ofrece alta resolución sobre todo en la zona focal y se degrada rápidamente fuera de la profundidad nominal.\n\n- **Principio SAFT**: el punto focal se trata como fuente virtual y el solapamiento de haces durante el barrido permite reconstruir una imagen más uniforme en profundidad.\n\n- **Validación experimental**:\n  1. **Validación 2D**: se emplearon fantomas de hilos de aluminio para cuantificar la mejora en profundidad de campo y relación señal-ruido mediante PSF.\n\n![image_saft](images/projects/image_saft.webp)\n\n  2. **Validación 3D**: un fantoma de rejilla impreso en 3D y escaneado a 4 mm y 10 mm mostró mayor nitidez con pseudo-3D SAFT.\n\n![saft_eq](images/projects/saft_eq.webp)\n\n- **Herramientas software**: implementación en MATLAB y visualización Viewer3D en modo MIP.\n\nEl proyecto muestra que SAFT puede mejorar mucho la calidad de imagen sin añadir electrónica compleja.',
        },
        acousticOutputTable: {
          title: 'Tabla de salida acústica para escáner ultrasónico Supersonic Imagine Mach',
          bodyText:
            '- Este proyecto desarrolló una Acoustic Output Table (AOT) para escáneres Supersonic Imagine Mach, cubriendo indicadores de seguridad como MI, PII, TIS, TIB y TIC.\n\nAspectos clave:\n\n1. **Proceso de medida**:\n   - mediciones con hidrófono de alta precisión de formas de onda de presión acústica\n   - cartografía espacial y temporal en tanque de agua\n\n2. **Cálculo de parámetros**:\n   - MI para evaluar riesgos no térmicos como cavitación\n   - PII para cuantificar energía por unidad de superficie\n   - TIS, TIB y TIC para evaluación térmica en tejido blando, interfaz ósea y cráneo\n\n3. **Cumplimiento de seguridad**:\n   - alineación con IEC 60601-2-37 y principios ALARA\n   - seguimiento de MI y TI para operación clínica segura\n\n4. **Informes y visualización**:\n   - tablas resumen por modo de imagen\n   - representaciones gráficas como mapas de isocontorno de intensidad\n\nEl proyecto resalta que la precisión en medida y reporte es la base de la seguridad y del rendimiento diagnóstico.',
        },
        waverKGpuWorkbench: {
          title: 'Waver : Ultrasound Simulation Software',
          introText:
            '- Se desarrolló un entorno de software de simulación ultrasónica para estudiar propagación, transmisión focalizada, steering electrónico y calidad de haz desde una sola interfaz.\n- El software combina una GUI en Python con backends numéricos acelerados, útil tanto para iteración de I+D como para demostración técnica.',
          stackText:
            '**Stack de código**\n- **Python** para la capa de aplicación y la orquestación del flujo.\n- **NiceGUI** para el panel de control interactivo.\n- **NumPy** y **Plotly** para postprocesado numérico y visualización del haz.\n- **CuPy** para propagación k-space / PSTD acelerada por GPU en el flujo `kgpu`.\n- **Rust** para el núcleo `waver_rust`, con **PyO3** y **Maturin** para exponer código compilado a Python.',
          bodyText:
            '**Qué permite el software**\n1. **Modos de fuente**: simular ondas planas, pistones, pistones focalizados y phased arrays.\n2. **Opciones del arreglo**: definir número de elementos, pitch, kerf, apertura, profundidad focal y objetivos laterales de steering.\n3. **Control de pulso**: ajustar frecuencia central, longitud del pulso en ciclos y número de frames exportados.\n4. **Configuración del medio**: construir medios multicapa con presets como agua, tejido blando, hueso cortical, acero inoxidable, aluminio o materiales personalizados.\n5. **Malla y bordes**: ajustar tamaño del dominio, muestreo espacial y bordes absorbentes PML.\n6. **Colocación de dispersores**: ubicar dispersores manualmente en cualquier punto del dominio de propagación para crear escenarios de dispersión a medida.\n7. **Análisis**: revisar animaciones de presión o envolvente, mapas CW de haz, estimaciones FWHM, respuestas impulsionales y exportación CSV, PNG y MP4.\n\n![Análisis FWHM](images/projects/waver-array-fwhm.webp)\n\n**Trabajo futuro**\n- Añadir mapas de tejido heterogéneo, bibliotecas de dispersores y presets multicapa más ricos.\n- Extender las simulaciones de transmisión hacia beamforming en recepción e imagen pulso-eco.\n- Añadir benchmarks automáticos para lóbulos laterales, desplazamiento focal y pérdida por steering.\n- Integrar bucles de optimización para diseño de arreglos y ajuste de secuencias.',
          heroMedia: {
            caption: 'Plane Wave propagation in water with Scatterers.',
          },
          mediaAfterIntro: [
            { caption: 'Campo de presión en transmisión focalizada.' },
            { caption: 'Mapa de haz asociado a la configuración focalizada.' },
          ],
          mediaAfterStack: [
            { caption: 'Campo de presión en transmisión steereda.' },
            { caption: 'Mapa de haz steerado mostrando el enfoque fuera de eje.' },
          ],
        },
        websiteDevelopment: {
          title: 'Desarrollo del sitio web JR-AI-Consulting',
          bodyText:
            '- El sitio web de JR-AI-Consulting fue creado para presentar la experiencia de la empresa en consultoria IA, analisis de datos y servicios de software.\n- El sitio ofrece una estructura profesional con secciones dedicadas a servicios, portfolio y contacto.\n- La pila tecnica combina **React** en frontend con servicios **Node.js** y backend **MongoDB**.',
        },
      },
    },
    about: {
      content:
        '&nbsp; &nbsp; &nbsp; **JR-AI-Consulting** aporta una amplia experiencia en ingeniería ultrasónica, respaldada por una base sólida en física y más de diez años de trayectoria en la industria del ultrasonido. El equipo ha trabajado en arquitectura de transductores, desarrollo de I+D, física ultrasónica de nivel senior y gestión de laboratorios.  \n\n&nbsp; &nbsp; &nbsp; En diseño y desarrollo de transductores, JR-AI-Consulting cuenta con experiencia demostrada y utiliza técnicas avanzadas de modelado como el modelo KLM y el análisis por elementos finitos. La empresa ha apoyado el desarrollo de transductores para aplicaciones médicas e industriales exigentes, con diseños optimizados según las restricciones reales de cada cliente.  \n\n&nbsp; &nbsp; &nbsp; La experiencia también se extiende al desarrollo de sistemas avanzados de imagen ultrasónica. Al combinar electrónica analógica T/X, CAD, impresión 3D, caracterización de materiales acústicos, modelado de haces, procesamiento de señal y gestión de proyectos, JR-AI-Consulting ha entregado prototipos innovadores, incluidos sistemas de imagen de alta frecuencia.  \n\n&nbsp; &nbsp; &nbsp; JR-AI-Consulting también ofrece apoyo especializado en física ultrasónica, incluyendo aseguramiento de calidad de sistemas y desarrollo de protocolos. Este trabajo ha contribuido a la validación del rendimiento de imagen y al alineamiento normativo junto a fabricantes y centros de investigación.  \n\n&nbsp; &nbsp; &nbsp; La competencia técnica cubre herramientas como Matlab, COMSOL, PZFlex/Onscale, Linux, Python, C++, SolidWorks y SQL. La empresa también conoce normas médicas clave como NEMA UD2, NEMA UD3, IEC 60601-2-37, IEC 62359, IEC 62127-1, ACT Accreditation, FDA (510k), ISO 13485 e ISO 9001.  \n\n&nbsp; &nbsp; &nbsp; Más allá del ultrasonido, **JR-AI-Consulting** también trabaja en **Consultoría científica**, **Cálculo numérico**, **Diseño web** y **Formación**, con apoyo experto para investigación, resolución de problemas técnicos, simulación avanzada, creación de sitios profesionales y programas formativos en física, matemáticas e ingeniería ultrasónica.  \n\n&nbsp; &nbsp; &nbsp; En **JR-AI-Consulting**, el enfoque sigue siendo la innovación, el rigor y la entrega práctica. La combinación entre profundidad técnica y ejecución orientada a producto ayuda a los clientes a resolver problemas reales y a hacer avanzar proyectos ambiciosos.',
    },
    footer: {
      copyright: 'JR-AI-Consulting © 2024. Todos los derechos reservados. Desarrollado por JR-AI-Consulting',
    },
    services: {
      noServices: 'No hay servicios disponibles.',
      scientificConsulting: {
        title: 'Consultoría científica',
        description:
          'Apoyo experto para estrategia de investigación, resolución de problemas técnicos y análisis de alto nivel alineados con los objetivos del cliente.',
      },
      numericalComputation: {
        title: 'Calculo numerico',
        description:
          'Modelado y simulación avanzada con Matlab, Python, COMSOL y herramientas adecuadas para decisiones técnicas fiables.',
      },
      webDesign: {
        title: 'Diseno web',
        description:
          'Sitios web profesionales, responsivos y centrados en el usuario, construidos con frameworks modernos y un objetivo de negocio claro.',
      },
      teachingTraining: {
        title: 'Formacion',
        description:
          'Programas de formación a medida en física, matemáticas, ingeniería ultrasónica y desarrollo de competencias técnicas.',
      },
      electronicInvoicing: {
        title: 'Factura electrónica',
        description:
          'Prepare su empresa para la factura electrónica con control de facturas, detección de campos faltantes, generación Factur-X y acompañamiento antes de la integración PDP.',
        ctaLabel: 'Reservar una demo',
      },
    },
  },
};

export const getTranslation = (language) => translations[language] || translations.en;
